<template>
  <div>
    <v-card class="my-1">
      <v-card-title>CP22 Form</v-card-title>
      <v-row class="px-4">
        <v-col>
          <v-row>
            <v-col>
              <v-menu
                v-model="startDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="startDateDisplay"
                    v-validate="'required'"
                    label="Start Date"
                    :error-messages="errors.collect('startDateDisplay')"
                    data-vv-name="startDateDisplay"  
                    data-vv-as="start date"                     
                    prepend-icon="mdi-calendar"
                    readonly
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="startDate"
                  @input="startDateChange"
                />
              </v-menu>
            </v-col>

            <v-col>
              <v-menu
                v-model="endDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on }">
                  <v-text-field
                    v-model="endDateDisplay"
                    v-validate="'required'"
                    label="End Date"
                    :disabled="!startDate"
                    :error-messages="errors.collect('endDateDisplay')"
                    data-vv-name="endDateDisplay"  
                    data-vv-as="end date" 
                    prepend-icon="mdi-calendar"
                    readonly
                    v-on="on"
                  />
                </template>
                <v-date-picker
                  v-model="endDate"
                  :min="startDate"
                  :max="latestDate"               
                  @input="endDateMenu = false"
                />
              </v-menu>
            </v-col>
          </v-row>
          
          <v-autocomplete
            v-model="selectedGroups"
            :items="groupOptions"
            label="Company Groups"
            item-text="text"
            item-value="value"
            multiple
            chips
            small-chips
            deletable-chips
            hide-selected
            clearable
          />

          <v-autocomplete
            v-model="selectedCompanies"
            v-validate="'required'"
            label="Companies"
            item-text="name"
            item-value="id"
            :items="companyOptions"
            clearable
            multiple
            hide-selected 
            chips
            small-chips
            deletable-chips
            :error-messages="errors.collect('selectedCompanies')"
            data-vv-name="selectedCompanies"  
            data-vv-as="companies"
          />

          <v-autocomplete
            v-if="!_.isEmpty(employees)"
            v-model="selectedEmployee"
            :loading="employeeDropdownLoading"
            label="Employee"
            item-text="name"
            item-value="id"
            :items="employees"
            hide-selected 
            chips
            small-chips
            :error-messages="errors.collect('selectedEmployee')"
            data-vv-name="selectedEmployee"  
            data-vv-as="employees"
          />
          
          <v-col class="mt-2">
            <span> Merge all in one file? </span>
            <v-radio-group
              v-model="isMerged"
              class="mt-0"
              row
            >
              <v-radio
                label="No"
                :value="0"
              />
              <v-radio
                label="Yes"
                :value="1"
              /> 
            </v-radio-group>
          </v-col>
        </v-col>
      </v-row>

      <v-container v-if="selectedEmployee !== null">
        <section-d
          :key="selectedEmployee"
          :detail="employee"
          @updated-detail="updatedDetail"
        />
      </v-container>

      <v-row>
        <v-col
          cols="2"
          class="ma-auto"
        >
          <v-row
            v-if="loading" 
            class="text-center"
          >
            <v-col cols="12">
              <div>
                <span class="font-weight-bold">Downloading</span>
              </div>
              <v-progress-circular
                :rotate="360"
                :size="50"
                :width="5"
                :value="progressbar"
                color="teal"
              >
                {{ progressbar }}
              </v-progress-circular>
            </v-col>
          </v-row>
      
          <v-row class="text-center">
            <v-col cols="12">
              <v-btn
                class="mx-3"
                color="primary"
                :loading="loading"
                :disabled="employees.length <= 0"
                @click="generateCP22Form"
              >
                generate
              </v-btn>
            </v-col>
          </v-row>
        </v-col>  
      </v-row>  
    </v-card>
  </div>
</template>

<script>
  export default {
    components: {
      SectionD: () => import('@/components/hris/payroll/report/income-tax-report/GovernmentCP22SectionD'),
    },    
    $_veeValidate: {
      validator: 'new', 
    },
    props: {
      companies: {
        type: Array,
        required: true,
      },
    },
    data () {
      return {
        loading: false,
        startDateMenu: false,
        startDate: null,
        endDateMenu: false,
        endDate: null,
        selectedGroups: [],
        selectedCompanies: [],
        employeeObj: null,
        employees: [],
        selectedEmployee: null,  
        employee: {}, 
        employeeDropdownLoading: false,
        progressbar: 0,
        isMerged: 0,
      }
    },
    computed: {      
      startDateDisplay () {
        return this.startDate ? this.$moment(this.startDate, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
      endDateDisplay () {
        return this.endDate ? this.$moment(this.endDate, 'YYYY-MM-DD').format('D MMM YYYY') : ''
      },
      computedFilter () {
        if (this.selectedCompanies.length < 1 || !this.startDate || !this.endDate) return

        return `${this.selectedCompanies}|${this.startDate}|${this.endDate}`
      },     
      latestDate () {
        if (this.startDate) {
          return this.$moment(this.startDate).endOf('year').format('YYYY-MM-DD')
        }
        return this.$moment().endOf('year').format('YYYY-MM-DD')
      },   
      groupOptions () {
        if (!this.companies) return []
        const groups = new Set()

        this.companies.forEach(company => {
          const groupVal = company.organization_lookup_uid
          if (!groupVal) return

          groups.add(company.group)
        })

        return Array.from(groups).map(group => ({
          text: `${(group || {}).value} Group`,
          value: group.lookup_uid,
        }))
      },
      filteredCompanies () {
        if (!this.selectedGroups.length) return this.companies

        return (this.companies || []).filter(company =>
          this.selectedGroups.includes(company.organization_lookup_uid)
        )
      },
      companyOptions () {
        const companies = (this.filteredCompanies || []).map(company => ({
          name: company.name,
          id: company.id,
        }))

        return [
          { name: 'All Companies', id: 'ALL' },
          ...companies,
        ]
      },    
    },    
    watch: {
      computedFilter: {
        immediate: true,
        handler (newVal) {
          if (newVal) {
            const [newCompanies, newStartDate, newEndDate] = newVal.split('|')
            if (newCompanies !== null && newStartDate !== null && newEndDate !== null) {
              this.fetchCP22Employee()
            }
          }
        },
      },
      selectedEmployee: {
        immediate: true,
        handler (newVal) {
          if (!_.isEmpty(newVal)) {
            this.employee = this.employees.find(employee => {
              return employee.id === newVal
            })
          }
        },
      },
      selectedGroups () {
        const validIds = (this.filteredCompanies || []).map(company => company.id)

        if (this.selectedCompanies.includes('ALL')) {
          this.selectedCompanies = [...validIds]
        } else {
          this.selectedCompanies = this.selectedCompanies.filter(id =>
            validIds.includes(id)
          )
        }
      },
      selectedCompanies (newVal) {
        const last = newVal[newVal.length - 1]

        // If last selected is 'ALL', keep only 'ALL'
        if (last === 'ALL' && newVal.length > 1) {
          // Replace with only 'ALL' after current update cycle
          this.$nextTick(() => {
            this.selectedCompanies = ['ALL']
          })
        } else if (newVal.includes('ALL') && newVal.length > 1) {
          // Remove 'ALL' if other company is selected after it
          this.$nextTick(() => {
            this.selectedCompanies = newVal.filter(id => id !== 'ALL')
          })
        }
      },
    },
    methods: {
      startDateChange () {
        this.startDateMenu = false
        this.endDateMenu = false
        this.endDate = this.$moment(this.startDate).endOf('month').format('YYYY-MM-DD')
      },      
      updatedDetail (value) {
        const empIndex = this.employees.findIndex(emp => emp.id === value.id)
        var salary = parseFloat(value.salary.replace(',', '')) * 100
        var bik = parseFloat(value.bik.replace(',', '')) * 100
        var vola = parseFloat(value.vola.replace(',', '')) * 100
        var commissionBonus = parseFloat(value.commissionBonus.replace(',', '')) * 100
        var akind = parseFloat(value.akind.replace(',', '')) * 100
        var leavePay = parseFloat(value.leavePay.replace(',', '')) * 100
        var acash = parseFloat(value.aCashAmt.replace(',', '')) * 100
        var other = parseFloat(value.otherAmt.replace(',', '')) * 100
        this.employees[empIndex].salary = salary
        this.employees[empIndex].bik = bik
        this.employees[empIndex].vola = vola
        this.employees[empIndex].commissionBonus = commissionBonus
        this.employees[empIndex].akind = akind
        this.employees[empIndex].leavePay = leavePay
        this.employees[empIndex].aCashType = value.aCashType
        this.employees[empIndex].aCashAmt = acash
        this.employees[empIndex].specifyOther = value.specifyOther
        this.employees[empIndex].otherAmt = other
        this.employees[empIndex].total = salary + bik + vola + commissionBonus + akind + leavePay + acash + other
      },
      fetchCP22Employee () {
        this.employeeDropdownLoading = true
        this.selectedEmployee = null
        this.employees = []

        this.$api.payrollReports().listCP22Employees(this.prepareFormData())
          .then(res => {
            if (res.data.length > 0) {
              res.data.map(data => {
                var salary = parseFloat(data.salary.replace(',', '')) * 100
                var bik = parseFloat(data.bik.replace(',', '')) * 100
                var vola = parseFloat(data.vola.replace(',', '')) * 100
                var commissionBonus = parseFloat(data.commissionBonus.replace(',', '')) * 100
                this.employeeObj = {
                  id: data.companyEmployee.company_employee_uid,
                  name: data.companyEmployee.employee.person.name + ' (' + data.companyEmployee.company.name + ')',
                  salary: salary,
                  bik: bik,
                  vola: vola,
                  commissionBonus: commissionBonus,
                  akind: 0,
                  leavePay: 0,
                  aCashType: '',
                  aCashAmt: 0,
                  specifyOther: '',
                  otherAmt: 0,
                  total: salary + bik + vola + commissionBonus,
                }
                this.employees.push(this.employeeObj)
                this.employees = _.orderBy(this.employees, ['name'], ['asc'])
              })
            } else {
              this.$snack({ 
                color: 'error',
                message: 'No Records Found', 
              })              
            }

            this.employeeDropdownLoading = false
          })
      },
      generateCP22Form () {
        this.$validator.validateAll()
          .then(result => {
            if (!result) return

            this.loading = true

            this.$api.payrollReports().employeeCP22(this.prepareFormData(), {
              onDownloadProgress: (progressEvent) => {
                this.progressbar = (Math.floor((progressEvent.loaded * 100) / progressEvent.total))
              },
            })
              .then(res => {
                let blob = new Blob([res.data], { type: 'application/zip' })
                let link = document.createElement('a')
                link.href = window.URL.createObjectURL(blob)
                link.setAttribute('download', 'CP22 FORM.zip') 
                link.click()
                this.loading = false
                this.progressbar = 0

                this.$snack({ message: 'CP22 Form downloaded successfully' })
              })
              .catch(error => {
                this.loading = false
                if (error.response.status === 400) {
                  this.$snack({ 
                    color: 'error',
                    message: error.response.data.status, 
                  })
                }
              })
          })
      },
      prepareFormData () {
        return {
          isMerged: this.isMerged,
          income_types: this.employees,
          start_date: this.startDate,
          end_date: this.endDate,
          companies: this.selectedCompanies.includes('ALL') ? (
            this.filteredCompanies.filter(company => {
              return company.id !== 'ALL'
            }).map(company => {
              return company.id
            })
          ) : this.selectedCompanies }
      },
    },
  }
</script>
